import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle, XCircle, ArrowRight, Warning } from "@phosphor-icons/react";
import { fmtMoney } from "@/lib/helpers";

const MAX_POLLS = 8;
const POLL_MS = 2000;

export function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ phase: "polling", data: null, tries: 1 });
  const timerRef = useRef(null);

  useEffect(() => {
    if (!sessionId) { setState({ phase: "invalid" }); return; }

    let mounted = true;
    let tries = 0;

    async function poll() {
      tries += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (!mounted) return;
        if (data.payment_status === "paid" || data.status === "completed") {
          setState({ phase: "success", data, tries });
          return;
        }
        if (data.status === "expired" || data.payment_status === "failed") {
          setState({ phase: "failed", data, tries });
          return;
        }
        if (tries >= MAX_POLLS) {
          setState({ phase: "timeout", data, tries });
          return;
        }
        setState({ phase: "polling", data, tries });
        timerRef.current = setTimeout(poll, POLL_MS);
      } catch (_e) {
        if (tries >= MAX_POLLS) {
          setState({ phase: "timeout", data: null, tries });
          return;
        }
        timerRef.current = setTimeout(poll, POLL_MS);
      }
    }
    poll();
    return () => { mounted = false; if (timerRef.current) clearTimeout(timerRef.current); };
  }, [sessionId]);

  const goToInvoice = () => {
    if (state.data?.invoice_id) navigate(`/billing/${state.data.invoice_id}`);
    else navigate("/billing");
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-background">
      <Card className="w-full max-w-md">
        <CardContent className="p-8 text-center space-y-5">
          {state.phase === "polling" && (
            <>
              <div className="h-14 w-14 rounded-full border-4 border-primary/20 border-t-primary animate-spin mx-auto" />
              <div>
                <div className="font-display text-2xl font-bold tracking-tight">Confirming your payment…</div>
                <p className="text-sm text-muted-foreground mt-1">Hang tight, this usually takes a second or two. (Attempt {state.tries}/{MAX_POLLS})</p>
              </div>
            </>
          )}
          {state.phase === "success" && (
            <>
              <div className="h-14 w-14 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center mx-auto">
                <CheckCircle size={40} weight="fill" />
              </div>
              <div>
                <div className="overline mb-1">Payment received</div>
                <div className="font-display text-3xl font-bold tracking-tight">Thank you!</div>
                <p className="text-sm text-muted-foreground mt-2">
                  {state.data?.invoice_no && (<>Invoice <b>{state.data.invoice_no}</b> · </>)}
                  {state.data?.amount && (<>{fmtMoney(state.data.amount)}</>)}
                </p>
              </div>
              <Button onClick={goToInvoice} className="w-full gap-1.5" data-testid="success-view-invoice">
                View invoice <ArrowRight size={14} />
              </Button>
            </>
          )}
          {state.phase === "failed" && (
            <>
              <div className="h-14 w-14 rounded-full bg-red-100 text-red-700 flex items-center justify-center mx-auto">
                <XCircle size={40} weight="fill" />
              </div>
              <div>
                <div className="font-display text-2xl font-bold tracking-tight">Payment failed</div>
                <p className="text-sm text-muted-foreground mt-2">Please try again or use another card.</p>
              </div>
              <Button variant="outline" onClick={goToInvoice} className="w-full">Back to invoice</Button>
            </>
          )}
          {state.phase === "timeout" && (
            <>
              <div className="h-14 w-14 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center mx-auto">
                <Warning size={36} weight="fill" />
              </div>
              <div>
                <div className="font-display text-2xl font-bold tracking-tight">Still processing…</div>
                <p className="text-sm text-muted-foreground mt-2">Your payment is taking longer than usual. Refresh the invoice in a minute — Stripe will finish confirming.</p>
              </div>
              <Button onClick={goToInvoice} className="w-full">Back to invoice</Button>
            </>
          )}
          {state.phase === "invalid" && (
            <>
              <div className="font-display text-2xl font-bold tracking-tight">No session found</div>
              <p className="text-sm text-muted-foreground">This link is missing a payment session id.</p>
              <Button onClick={() => navigate("/billing")} className="w-full">Go to billing</Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export function PaymentCancelPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const invoiceId = params.get("invoice_id");
  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-background">
      <Card className="w-full max-w-md">
        <CardContent className="p-8 text-center space-y-5">
          <div className="h-14 w-14 rounded-full bg-muted text-muted-foreground flex items-center justify-center mx-auto">
            <XCircle size={40} weight="duotone" />
          </div>
          <div>
            <div className="font-display text-2xl font-bold tracking-tight">Payment cancelled</div>
            <p className="text-sm text-muted-foreground mt-2">No charge was made. You can retry any time.</p>
          </div>
          <Button
            onClick={() => navigate(invoiceId ? `/billing/${invoiceId}` : "/billing")}
            className="w-full gap-1.5"
            data-testid="cancel-back"
          >
            Back to invoice <ArrowRight size={14} />
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
